import { call, fork, put, takeEvery } from 'redux-saga/effects';
import appActions from './actions';
import appToastMsg from '../../../constants/service/toastMsg/appToastMsg';
import AppApi from '../../../lib/api/app/AppApi';
import CouponApi from '../../../lib/api/coupon/CouponApi';
import {
  openNewWindow,
  setMultipartForm,
  setRequestForm,
  setResponseListForm,
  asyncSimpleModal,
  setDetailPageUrl,
} from '../../../lib/common';
import toastMsg from '../../../lib/toastMsg';
import { momentBusinessDay } from '../../../lib/moment';
import { PRODUCT_DETAIL_MODEL } from '../product/models';
import { IMG_ITEM_MODEL } from './models';
import { DATE_FORMAT } from '../../../constants/service/common/utilConstants';
import { TAG_CREATE_URL_PATH, TAG_EDIT_URL_PATH } from '../../../constants/service/common/urlConstants';
import { TAG_STATUS } from '../../../constants/service/app/tagConstants';
import { USER_ACTIONS_NAME } from '../../../constants/service/app/accountUserConstants';
import { extractErrorMessage } from '../../../lib/utils/checkErrorResponse';

function* fetchList() {
  /**
   *  기본적인 정보 가져오기
   */
  yield takeEvery(appActions.GET_COMMON_DATA_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getAppCommonData, setRequestForm(payload, false));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        /**
         *  공휴일 리스트 업데이트하기
         */
        if (result.data.holidayList && result.data.holidayList.length) {
          momentBusinessDay.updateLocale('ko', {
            holidays: result.data.holidayList,
            holidayFormat: DATE_FORMAT.YMD_DASH_FORMAT,
          });
        }

        /**
         *  admin level 받아오기
         */
        yield put({ type: appActions.GET_COMMON_DATA_SUCCESS, payload: result.data });
      }
    } catch (error) {
      toastMsg.error(extractErrorMessage(error), error);
    }
  });

  /**
   *  상품 상세 미리보기 데이터 요청하기
   */
  yield takeEvery(appActions.GET_PRODUCT_PREVIEW_DATA_REQUEST, function* ({ payload }) {
    try {
      const { productId, storeId, storeName, noticeContent, productNoticeId, disabledActiveValue } = payload;

      const result = yield call(
        AppApi.getProductPreviewData,
        setRequestForm(
          {
            productId,
            storeId,
            productNoticeId,
          },
          true,
        ),
      );
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_PRODUCT_PREVIEW_DATA_REQUEST_SUCCESS);

        /**
         * 모달에 내려줄 data를 정의한다.
         * productId 가 있으면 상품 상세 데이터를 사용하고
         * storeId 가 있으면 브랜드 이미지와 상품 기본 정보(model)를 사용한다.
         */
        let data;

        /**
         *  productId 가 유효한 경우
         */
        if (productId) {
          data = {
            ...result.data.productPreviewData,
            /**
             *  앱 노출 화면 탭 비 활성화
             */
            disabledActiveValue,
          };
        } else if (storeId) {
          /**
           *  storeId 가 유효한 경우
           */
          const productDetail = PRODUCT_DETAIL_MODEL;

          data = {
            mainImg: productDetail.proImg.mainImg,
            subImg: productDetail.proImg.subImg,
            storeName: storeName,
            storeImg: result.data.productPreviewData.storeImg,
            /**
             *  제품 정보 (가격, 상품명)
             */
            name: productDetail.proNameAndPrice.name,
            isDiscount: !!productDetail.proNameAndPrice.discountAmount,
            salesPrice: productDetail.proNameAndPrice.salesPrice,
            originPrice: productDetail.proNameAndPrice.originPrice,
            discountRate: productDetail.proNameAndPrice.discountRate,
            /**
             *  공지사항 정보
             */
            productNotice: {
              startDate: result.data.productPreviewData.productNoticePeriod.startDate,
              endDate: result.data.productPreviewData.productNoticePeriod.endDate,
              content: noticeContent,
            },
            /**
             *  상품 상세 미리보기 모달 우측 상단에 상점, 상품 상태 값 노출 여부
             */
            isShowStatusValue: !productNoticeId,
            /**
             *  상세 이미지
             */
            detailImg: productDetail.proDescriptionImg.detailImg,
            /**
             *  상품 상세 설명
             */
            tagLinkData: {},
            size: productDetail.proInfo.size,
            weight: productDetail.proInfo.weight,
            /**
             *  배송 관련 안내
             */
            deliveryChargeEtc: productDetail.proStatusAndBrandInfo.deliveryChargeEtc,
            deliveryChargeJeju: productDetail.proStatusAndBrandInfo.deliveryChargeJeju,
            /**
             *  반품 교환 안내
             */
            memo: productDetail.proDeliveryInfo.returnAddress.info,
            line1: productDetail.proDeliveryInfo.returnAddress.line1,
            line2: productDetail.proDeliveryInfo.returnAddress.line2,
            /**
             *  A/S 정보
             */
            asInform: productDetail.proAfterService.asInform,
            caution: productDetail.proAfterService.caution,
            qualityAssurance: productDetail.proAfterService.qualityAssurance,
            origin: productDetail.proAfterService.origin,
            manufacturer: productDetail.proAfterService.manufacturer,
            /**
             *  브랜드 연락처
             */
            storePhone: productDetail.proStatusAndBrandInfo.storePhone,
            asPhone: productDetail.proAfterService.asPhone,
            kakaoAccount: productDetail.proAfterService.kakaoAccount,
            /**
             *  상품 배송 타입
             */
            deliveryType: result.data.productPreviewData.deliveryType,
            /**
             *  주문 시 제작 제품 여부
             */
            isOrderMade: result.data.productPreviewData.isOrderMade,
            /**
             *  주문 시 제작 소요시간
             */
            orderMadeDate: result.data.productPreviewData.orderMadeDate,
            /**
             *  바로 배송 기준 시간
             */
            directDeliveryTimeLimit: result.data.productPreviewData.directDeliveryTimeLimit,
            /**
             *  앱 노출 화면 탭 비 활성화
             */
            disabledActiveValue,
          };
        }

        yield put(
          appActions.appToggleModal({
            name: 'productDetailPreviewModal',
            value: true,
            data,
          }),
        );
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_PRODUCT_PREVIEW_DATA_REQUEST_ERROR, error);
    }
  });

  /**
   *  태그 상세 미리보기 데이터 요청하기
   */
  yield takeEvery(appActions.GET_TAG_PREVIEW_DATA_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getTagPreviewData, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_TAG_PREVIEW_DATA_REQUEST_SUCCESS);

        const tagPreviewData = {
          tagInfo: {
            tagId: result.data.tagInfo.id,
            tagName: result.data.tagInfo.name,
          },
          tagMainTab: {
            itemList: result.data.tagMainTabItemList,
          },
          tagViewInfo: result.data.tagViewInfo,
          tagDetailPage: {
            relatedTagList: result.data.relatedTagList,
            itemList: result.data.tagDetailItemList,
          },
        };

        yield put(
          appActions.appToggleModal({
            name: 'tagPreviewModal',
            value: true,
            data: {
              tagPreviewData,
            },
          }),
        );
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_TAG_PREVIEW_DATA_REQUEST_ERROR, error);
    }
  });

  /**
   *  주문 배송 조회 데이터 요청하기
   */
  yield takeEvery(appActions.GET_ORDER_DELIVERY_TRACKING_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getOrderDeliveryTrackingData, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_ORDER_DELIVERY_TRACKING_REQUEST_SUCCESS);

        yield put(
          appActions.appToggleModal({
            name: 'deliveryTrackingModal',
            value: true,
            data: result.data.deliveryTrackingData,
          }),
        );
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_ORDER_DELIVERY_TRACKING_REQUEST_ERROR, error);
    }
  });

  /**
   *  배송지 변경 이력 조회 데이터 요청하기
   */
  yield takeEvery(appActions.GET_ORDER_DELIVERY_MODIFY_DATA_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getOrderDeliveryModifyData, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_ORDER_DELIVERY_MODIFY_DATA_REQUEST_SUCCESS);

        yield put(
          appActions.appToggleModal({
            name: 'orderModifyDeliveryInfoModal',
            value: true,
            data: {
              orderDeliveryModifyData: result.data.orderDeliveryModifyData,
            },
          }),
        );
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_ORDER_DELIVERY_MODIFY_DATA_REQUEST_ERROR, error);
    }
  });

  /**
   *  기획전 아이템 리스트 요청하기
   */
  yield takeEvery(appActions.GET_EVENT_ITEM_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getEventItemList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_EVENT_ITEM_LIST_REQUEST_SUCCESS);
        const data = {
          listData: setResponseListForm(result.data.eventItemList, result.data.totalCnt, payload),
        };

        yield put({ type: appActions.GET_EVENT_ITEM_LIST_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_EVENT_ITEM_LIST_REQUEST_ERROR, error);
    }
  });

  /**
   *  브랜드 상품 리스트 요청하기
   */
  yield takeEvery(appActions.GET_BRAND_PRODUCT_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getBrandProductList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_BRAND_PRODUCT_LIST_REQUEST_SUCCESS);

        const data = {
          listData: setResponseListForm(result.data.brandProductList, result.data.totalCnt, payload),
        };

        yield put({ type: appActions.GET_BRAND_PRODUCT_LIST_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_BRAND_PRODUCT_LIST_REQUEST_ERROR, error);
    }
  });

  /**
   *  상품 공지사항 아이템 리스트 요청하기
   */
  yield takeEvery(appActions.GET_NOTICE_PRODUCT_ITEM_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getNoticeProductItemList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_NOTICE_PRODUCT_ITEM_LIST_REQUEST_SUCCESS);
        const data = {
          listData: setResponseListForm(result.data.noticeProductItemList, result.data.totalCnt, payload),
        };

        yield put({ type: appActions.GET_NOTICE_PRODUCT_ITEM_LIST_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_NOTICE_PRODUCT_ITEM_LIST_REQUEST_ERROR, error);
    }
  });

  /**
   *  문자 발송 요청하기전 수신자 정보와 관리자 권한 체크하기
   */
  yield takeEvery(appActions.PREPARE_SEND_MESSAGE_SERVICE_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.prepareSendMessageService, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.SEND_MESSAGE_SERVICE_REQUEST_SUCCESS);

        yield put(
          appActions.appToggleModal({
            name: 'sendMessageServiceModal',
            value: true,
            data: {
              memberType: payload.memberType,
              receiverInfo: {
                targetId: payload.targetId,
                cellPhone: payload.cellPhone,
                ...result.data.receiverInfo,
              },
            },
          }),
        );
      }
    } catch (error) {
      toastMsg.error(appToastMsg.SEND_MESSAGE_SERVICE_REQUEST_ERROR, error);
    }
  });

  /**
   *  문자 발송 요청하기
   */
  yield takeEvery(appActions.SEND_MESSAGE_SERVICE_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.sendMessageService, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.SEND_MESSAGE_SERVICE_REQUEST_SUCCESS);

        yield put(
          appActions.appToggleModal({
            name: 'sendMessageServiceModal',
            value: false,
            data: {},
          }),
        );
      }
    } catch (error) {
      toastMsg.error(appToastMsg.SEND_MESSAGE_SERVICE_REQUEST_ERROR, error);
    }
  });

  /**
   *  이미지 임시로 저장하기
   */
  yield takeEvery(appActions.ADD_TEMP_IMG_REQUEST, function* ({ payload }) {
    try {
      const { setData, targetPath, info, requestData } = payload;

      const result = yield call(AppApi.addTempImg, setMultipartForm(requestData, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.ADD_TEMP_IMG_REQUEST_SUCCESS);

        /**
         *  이미지 아이템
         */
        const imgItemModel = { ...IMG_ITEM_MODEL };
        imgItemModel.id = result.data.id;
        imgItemModel.url = result.data.url;
        imgItemModel.path = result.data.path;
        imgItemModel.contentType = info.contentType;
        imgItemModel.height = info.height;
        imgItemModel.width = info.width;
        imgItemModel.isEdit = true;

        setData({
          path: targetPath,
          value: {
            ...imgItemModel,
          },
        });
      }
    } catch (error) {
      toastMsg.error(appToastMsg.ADD_TEMP_IMG_REQUEST_ERROR, error);
    }
  });

  /**
   *  쿠폰 발급 유효성 체크 요청하기
   */
  yield takeEvery(appActions.CHECK_VALID_GENERATE_COUPON_REQUEST, function* ({ payload }) {
    try {
      const { selectedUserIdList, selectedCouponInfoList, targetAction } = payload;

      const result = yield call(
        AppApi.checkValidGenerateCoupon,
        setRequestForm({ selectedUserIdList, selectedCouponInfoList }, true),
      );
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.CHECK_VALID_GENERATE_COUPON_REQUEST_SUCCESS);

        const { userCouponList, requestCnt, possibleCnt, impossibleCnt, isBulk } = result.data;

        /**
         * 실패건이 하나라도 있는 경우에는 실패 모달로 보낸다.
         */
        if (impossibleCnt > 0) {
          /**
           *  쿠폰 발급 유효 체크 실패 모달 띄우기
           */
          yield put(
            appActions.appToggleModal({
              name: 'generateCouponValidFailModal',
              value: true,
              data: {
                userCouponList,
                requestCnt,
                possibleCnt,
                impossibleCnt,
                isBulk,
              },
            }),
          );
        } else {
          /**
           *  쿠폰 발급 유효 체크 성공 모달 띄우기
           */
          yield put(
            appActions.appToggleModal({
              name: 'generateCouponValidSuccessModal',
              value: true,
              data: {
                userCouponList,
                requestCnt,
                possibleCnt,
                impossibleCnt,
                targetAction,
              },
            }),
          );
        }
      }
    } catch (error) {
      toastMsg.error(appToastMsg.CHECK_VALID_GENERATE_COUPON_REQUEST_ERROR);
    }
  });

  /**
   *  [쿠폰 발급] 발급할 쿠폰 리스트 가져오기
   */
  yield takeEvery(appActions.GET_ISSUED_COUPON_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(CouponApi.getGenerateSearchCouponList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GET_ISSUED_COUPON_LIST_REQUEST_SUCCESS);

        const data = {
          couponList: setResponseListForm(result.data.couponList, result.data.totalCnt, payload),
        };

        yield put({ type: appActions.GET_ISSUED_COUPON_LIST_SUCCESS, payload: data });
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GET_ISSUED_COUPON_LIST_REQUEST_ERROR);
    }
  });

  /**
   *  [쿠폰 발급] 쿠폰 발급 요청하기
   */
  yield takeEvery(appActions.GENERATE_COUPON_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.generateCoupon, setRequestForm(payload, true));

      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GENERATE_COUPON_REQUEST_SUCCESS);

        /**
         *  쿠폰 발급 페이지, 유저 상세 페이지에서의 쿠폰 발급 하고나서의 액션이 다르다.
         *
         *  쿠폰 발급 페이지: 테이블들의 리스트들을 초기화해야한다.
         *  유저 상세 페이지: 발급한 쿠폰 정보를 테이블에 넣어줘야한다.
         */
        if (payload.targetAction.name === USER_ACTIONS_NAME) {
          const data = {
            couponHistoryList: result.data.couponHistoryList.list,
            totalCnt: result.data.couponHistoryList.totalCnt,
          };

          yield put({ type: payload.targetAction.GENERATE_COUPON_SUCCESS, payload: data });
        } else {
          yield put({ type: payload.targetAction.GENERATE_COUPON_SUCCESS });
        }
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GENERATE_COUPON_REQUEST_ERROR);
    }
  });

  /**
   *  반품 요청, 반품 거부, 교환 요청, 교환 거부 이미지 가져오기
   */
  yield takeEvery(appActions.GET_REQUEST_REJECT_ATTACHMENT_DATA_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getRequestRejectAttachmentData, setRequestForm(payload, false));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      const data = {
        attachments: result.data.targetAttachments,
        title: payload.title,
        reason: payload.reason,
      };

      /**
       *  반품 요청, 반품 거부, 교환 요청, 교환 거부 이미지 모달 열기
       */
      yield put(
        appActions.appToggleModal({
          name: 'returnExchangeReasonModal',
          data,
        }),
      );
    } catch (e) {
      toastMsg.error(appToastMsg.GET_REQUEST_REJECT_ATTACHMENT_DATA_REQUEST_ERROR, e);
    }
  });

  /**
   *  포인트 발급 유효성 체크 요청하기
   */
  yield takeEvery(appActions.CHECK_VALID_GENERATE_POINT_REQUEST, function* ({ payload }) {
    try {
      const { selectedUserInfoList, targetAction } = payload;
      const result = yield call(AppApi.checkValidGeneratePoint, setRequestForm({ selectedUserInfoList }, true));

      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.CHECK_VALID_GENERATE_POINT_REQUEST_SUCCESS);

        const { userPointList, pointCauseSelectList, requestCnt, possibleCnt, impossibleCnt, isBulk } = result.data;

        /**
         * 실패건이 하나라도 있는 경우에는 실패 모달로 보낸다.
         */
        if (impossibleCnt > 0) {
          /**
           *  포인트 발급 유효 체크 실패 모달 띄우기
           */
          yield put(
            appActions.appToggleModal({
              name: 'generatePointValidFailModal',
              value: true,
              data: {
                userPointList,
                pointCauseSelectList,
                requestCnt,
                possibleCnt,
                impossibleCnt,
                isBulk,
              },
            }),
          );
        } else {
          /**
           *  포인트 발급 유효 체크 성공 모달 띄우기
           */
          yield put(
            appActions.appToggleModal({
              name: 'generatePointValidSuccessModal',
              value: true,
              data: {
                userPointList,
                pointCauseSelectList,
                requestCnt,
                possibleCnt,
                impossibleCnt,
                targetAction,
              },
            }),
          );
        }
      }
    } catch (error) {
      toastMsg.error(appToastMsg.CHECK_VALID_GENERATE_POINT_REQUEST_ERROR);
    }
  });

  /**
   *  포인트 발급 하기
   */
  yield takeEvery(appActions.GENERATE_POINT_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.generatePoint, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(appToastMsg.GENERATE_POINT_REQUEST_SUCCESS);

        /**
         *  포인트 발급이 유저 상세 페이지 or 포인트 발급 페이지에서 했는지에 따라서 분기한다.
         */
        if (payload.targetAction.name === USER_ACTIONS_NAME) {
          /**
           * user 페이지에서 발급한거면 user 상세 페이지의 포인트 내역 업데이트
           */
          const data = {
            pointLog: result.data.pointLog,
            pointInfo: result.data.pointInfo,
          };

          yield put({ type: payload.targetAction.GENERATE_POINT_SUCCESS, payload: data });
        } else {
          /**
           *  point 페이지에서 발급한거면 point 페이지 초기화
           */
          yield put({ type: payload.targetAction.GENERATE_POINT_SUCCESS });
        }
      }
    } catch (error) {
      toastMsg.error(appToastMsg.GENERATE_POINT_REQUEST_ERROR);
    }
  });

  /**
   *  태그 유효성 검사
   */
  yield takeEvery(appActions.CHECK_TAG_NAME_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(
        AppApi.checkTag,
        setRequestForm({
          name: payload.name,
        }),
      );
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      const { tagId, groupId, status } = result.data;

      /**
       *  태그는 존재하지만 tagGroupId 가 존재하지 않거나 상태가 활성이 아닌 경우
       */
      if (tagId) {
        /**
         *  tagGroupId 일치하지 않는 경우
         */
        if (groupId !== payload.groupId) {
          yield asyncSimpleModal({
            type: 'info',
            title: `"${payload.name}" 태그 편집`,
            message: `해당 브랜드에 "${payload.name}" 태그를 연결하기 위해서는\n "${payload.name}" 태그를 브랜드 컨셉 태그로 등록해야 합니다. \n "${payload.name}" 태그 편집페이지에서 편집해주세요. `,
          });
        } else if (status !== TAG_STATUS.active) {
          /**
           *  태그 상태가 활성 상태가 아닌 경우
           */
          yield asyncSimpleModal({
            type: 'info',
            title: `"${payload.name}" 태그 편집`,
            message: `해당 브랜드에 "${payload.name}" 태그를 연결하기 위해서는\n "${payload.name}" 태그를 활성 상태로 변경해야 합니다. \n "${payload.name}" 태그 편집페이지에서 편집해주세요. `,
          });
        }

        /**
         *  해당 태그 편집 페이지로 이동
         */
        openNewWindow(setDetailPageUrl(TAG_EDIT_URL_PATH, tagId));
      } else if (!tagId) {
        /**
         *  태그가 존재하지 않는 경우
         */
        yield asyncSimpleModal({
          type: 'info',
          title: `"${payload.name}" 태그 등록`,
          message: `해당 브랜드에 "${payload.name}" 태그를 연결하기 위해서는\n "${payload.name}" 태그를 브랜드 컨셉 태그로 등록해야 합니다. \n 태그 등록페이지창에서 "${payload.name}" 태그를 등록해주세요. `,
        });

        /**
         *  태그등록 페이지로 이동
         */
        openNewWindow(TAG_CREATE_URL_PATH);
      }
    } catch (e) {
      toastMsg.error(appToastMsg.CHECK_TAG_NAME_REQUEST_ERROR, e);
    }
  });

  /**
   *  연관된 태그 리스트 요청하기
   */
  yield takeEvery(appActions.GET_RELATED_TAG_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getRelatedTagList, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      yield put(
        appActions.appToggleModal({
          name: 'tagListViewModal',
          data: result.data,
        }),
      );
    } catch (e) {
      toastMsg.error(appToastMsg.GET_RELATED_TAG_LIST_REQUEST_ERROR, e);
    }
  });

  /**
   *  상품 2차 카테고리 리스트 가져오기
   */
  yield takeEvery(appActions.GET_PRODUCT_SUB_CATEGORY_LIST_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getProductSubCategoryList, setRequestForm(payload));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      /**
       *  상품 2차 카테고리 보기 모달 띄우기
       */
      yield put(
        appActions.appToggleModal({
          name: 'productSubCategoryListViewModal',
          data: result.data,
          value: true,
        }),
      );
    } catch (e) {
      toastMsg.error(appToastMsg.GET_PRODUCT_SUB_CATEGORY_LIST_REQUEST_ERROR, e);
    }
  });

  /**
   *  구매자 이전 구매내역 조회 데이터 가져오기
   */
  yield takeEvery(appActions.GET_BUYER_PURCHASE_HISTORY_REQUEST, function* ({ payload }) {
    try {
      const result = yield call(AppApi.getBuyerPurchaseHistoryData, setRequestForm(payload, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      const data = {
        buyerHistoryInfo: result.data.buyerHistoryInfo,
        buyerHistoryList: result.data.buyerHistoryList,
      };

      yield put(
        appActions.appToggleModal({
          name: 'buyerPurchaseHistoryModal',
          data,
        }),
      );
    } catch (e) {
      toastMsg.error(appToastMsg.GET_BUYER_PURCHASE_HISTORY_REQUEST_ERROR, e);
    }
  });

  /**
   *  택배사 리스트 가져오기
   */
  yield takeEvery(appActions.GET_DELIVERY_AGENT_LIST_REQUEST, function* () {
    try {
      const result = yield call(AppApi.getDeliveryAgentList, setRequestForm({}, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      /**
       *  택배사 리스트를 simpleAsyncModal 로 보여주도록 한다.
       */
      yield asyncSimpleModal({
        type: 'info',
        title: '택배사 리스트',
        message: result.data.deliveryAgentList.map((item) => `${item.id + '. ' + item.name + '\n'}`),
      });
    } catch (e) {
      toastMsg.error(appToastMsg.GET_ORDER_DELIVERY_TRACKING_REQUEST_ERROR, e);
    }
  });

  /**
   *  아이템 검색 리스트 가져오기
   */
  yield takeEvery(appActions.GET_PRODUCT_CATEGORY_SEARCH_LIST_REQUEST, function* () {
    try {
      const result = yield call(AppApi.getProductCategoryItemSearchList, setRequestForm({}, true));
      if (result.msg) {
        toastMsg.error(result.msg);
        return;
      }

      yield put({ type: appActions.GET_PRODUCT_CATEGORY_SEARCH_LIST_SUCCESS, payload: result.data });
    } catch (e) {
      toastMsg.error(appToastMsg.GET_PRODUCT_CATEGORY_SEARCH_LIST_REQUEST_ERROR, e);
    }
  });
}

export default function* baseSaga() {
  yield fork(fetchList);
}
